import React from 'react'
import _ from 'lodash'
import { GOOGLE_STATUS } from '@config/constants'

/**
 * Configuration options for GMB location formatting
 */
export const GMB_LOCATION_CONFIG = {
  ICON_TYPES: {
    EMOJI: 'emoji',
    COMPONENT: 'component',
    NONE: 'none'
  },
  ADDRESS_FORMATS: {
    FULL: 'full',
    SHORT: 'short',
    CITY_STATE: 'city-state'
  },
  STYLING: {
    DEFAULT: 'default',
    COMPACT: 'compact',
    DETAILED: 'detailed'
  }
}

/**
 * Default configuration for GMB location formatting
 */
const DEFAULT_CONFIG = {
  showIcon: true,
  iconType: GMB_LOCATION_CONFIG.ICON_TYPES.EMOJI,
  addressFormat: GMB_LOCATION_CONFIG.ADDRESS_FORMATS.FULL,
  styling: GMB_LOCATION_CONFIG.STYLING.DEFAULT,
  filterVerified: true,
  customIcon: null
}

/**
 * Formats address components based on the specified format
 * @param {Object} storefrontAddress - Address object from GMB API
 * @param {string} format - Address format type
 * @returns {string} Formatted address string
 */
export const formatGMBAddress = (storefrontAddress, format = GMB_LOCATION_CONFIG.ADDRESS_FORMATS.FULL) => {
  if (!storefrontAddress) return ''

  const addressLines = _.get(storefrontAddress, 'addressLines', [])
  const locality = _.get(storefrontAddress, 'locality')
  const administrativeArea = _.get(storefrontAddress, 'administrativeArea')
  const postalCode = _.get(storefrontAddress, 'postalCode')

  switch (format) {
    case GMB_LOCATION_CONFIG.ADDRESS_FORMATS.SHORT:
      return [locality, administrativeArea].filter(Boolean).join(', ')

    case GMB_LOCATION_CONFIG.ADDRESS_FORMATS.CITY_STATE:
      return [locality, administrativeArea, postalCode].filter(Boolean).join(', ')

    case GMB_LOCATION_CONFIG.ADDRESS_FORMATS.FULL:
    default:
      return [...addressLines, locality, administrativeArea, postalCode].filter(Boolean).join(', ')
  }
}

/**
 * Gets styling configuration based on styling type
 * @param {string} stylingType - Type of styling to apply
 * @returns {Object} Style configuration object
 */
export const getGMBStyling = (stylingType = GMB_LOCATION_CONFIG.STYLING.DEFAULT) => {
  const baseStyles = {
    container: {
      padding: '4px 0',
      lineHeight: '1.2'
    },
    title: {
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '2px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'center'
    },
    address: {
      color: '#666',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    icon: {
      fontSize: '12px',
      color: '#666'
    }
  }

  switch (stylingType) {
    case GMB_LOCATION_CONFIG.STYLING.COMPACT:
      return {
        ...baseStyles,
        container: {
          ...baseStyles.container,
          padding: '2px 0',
          lineHeight: '1.1'
        },
        title: {
          ...baseStyles.title,
          fontSize: '13px',
          marginBottom: '1px'
        },
        address: {
          ...baseStyles.address,
          fontSize: '11px'
        }
      }

    case GMB_LOCATION_CONFIG.STYLING.DETAILED:
      return {
        ...baseStyles,
        container: {
          ...baseStyles.container,
          padding: '6px 0',
          lineHeight: '1.3'
        },
        title: {
          ...baseStyles.title,
          fontSize: '15px',
          marginBottom: '3px'
        },
        address: {
          ...baseStyles.address,
          fontSize: '13px'
        }
      }

    case GMB_LOCATION_CONFIG.STYLING.DEFAULT:
    default:
      return {
        ...baseStyles,
        title: {
          ...baseStyles.title,
          fontSize: '14px'
        },
        address: {
          ...baseStyles.address,
          fontSize: '12px'
        }
      }
  }
}

/**
 * Renders the icon for GMB location options
 * @param {Object} config - Configuration object
 * @returns {React.Element|null} Icon element or null
 */
export const renderGMBLocationIcon = (config) => {
  if (!config.showIcon) return null

  const iconStyle = {
    marginRight: '5px',
    ...getGMBStyling(config.styling).icon
  }

  switch (config.iconType) {
    case GMB_LOCATION_CONFIG.ICON_TYPES.EMOJI:
      return (
        <span className="mr-5" style={iconStyle} role="img" aria-label="Location">
          📍
        </span>
      )

    case GMB_LOCATION_CONFIG.ICON_TYPES.COMPONENT:
      // For when using Icons component (can be passed as customIcon)
      return config.customIcon ? (
        <i className="mr-5" style={iconStyle}>
          {config.customIcon}
        </i>
      ) : null

    case GMB_LOCATION_CONFIG.ICON_TYPES.NONE:
    default:
      return null
  }
}

/**
 * Core formatting function for GMB location options
 * This is the main function that creates the formatted option label
 * @param {Object} option - The select option object
 * @param {Object} config - Configuration options
 * @returns {React.Element} Formatted option element
 */
export const formatGMBLocationOption = (option, config = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config }
  const styles = getGMBStyling(finalConfig.styling)
  const formattedAddress = formatGMBAddress(_.get(option.value, 'storefrontAddress'), finalConfig.addressFormat)

  return (
    <div style={styles.container}>
      <div style={styles.title}>
        {renderGMBLocationIcon(finalConfig)}
        {option.value.title}
      </div>
      {formattedAddress && <div style={styles.address}>{formattedAddress}</div>}
    </div>
  )
}

/**
 * Creates location options array for react-select with enhanced formatting
 * Enhanced version of the existing gmbLocationOptions function
 * @param {Array} locations - Array of GMB location objects
 * @param {Object} config - Configuration options
 * @returns {Array} Array of formatted options for react-select
 */
export const createGMBLocationOptions = (locations, config = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config }

  if (!locations || !Array.isArray(locations)) {
    return []
  }

  let filteredLocations = locations

  // Filter verified locations if requested
  if (finalConfig.filterVerified) {
    const disableVerification = process.env.REACT_APP_ENV !== 'production' && process.env.REACT_APP_ENV !== 'live'
    if (!disableVerification) {
      filteredLocations = locations.filter((location) => location.verificationState === GOOGLE_STATUS.COMPLETED)
    }
  }

  return filteredLocations.map((location, index) => ({
    value: location,
    label: location.title,
    // Store config for use in formatOptionLabel
    _gmbConfig: finalConfig
  }))
}

/**
 * Ready-to-use formatOptionLabel function for react-select
 * This can be passed directly to the formatOptionLabel prop
 * @param {Object} config - Configuration options
 * @returns {Function} formatOptionLabel function
 */
export const createGMBLocationFormatter = (config = {}) => {
  return (option) => formatGMBLocationOption(option, config)
}

/**
 * Complete package: options + formatter function
 * Returns both the options array and the formatOptionLabel function
 * @param {Array} locations - Array of GMB location objects
 * @param {Object} config - Configuration options
 * @returns {Object} Object containing options and formatOptionLabel
 */
export const createGMBLocationSelectPackage = (locations, config = {}) => {
  const options = createGMBLocationOptions(locations, config)
  const formatOptionLabel = createGMBLocationFormatter(config)

  return {
    options,
    formatOptionLabel,
    config: { ...DEFAULT_CONFIG, ...config }
  }
}

/**
 * Preset configurations for common use cases
 */
export const GMB_PRESETS = {
  // Default preset - same as current implementation
  DEFAULT: {
    showIcon: true,
    iconType: GMB_LOCATION_CONFIG.ICON_TYPES.EMOJI,
    addressFormat: GMB_LOCATION_CONFIG.ADDRESS_FORMATS.FULL,
    styling: GMB_LOCATION_CONFIG.STYLING.DEFAULT
  },

  // Compact preset for mobile or tight spaces
  COMPACT: {
    showIcon: true,
    iconType: GMB_LOCATION_CONFIG.ICON_TYPES.EMOJI,
    addressFormat: GMB_LOCATION_CONFIG.ADDRESS_FORMATS.SHORT,
    styling: GMB_LOCATION_CONFIG.STYLING.COMPACT
  },

  // Detailed preset for desktop or important selections
  DETAILED: {
    showIcon: true,
    iconType: GMB_LOCATION_CONFIG.ICON_TYPES.EMOJI,
    addressFormat: GMB_LOCATION_CONFIG.ADDRESS_FORMATS.FULL,
    styling: GMB_LOCATION_CONFIG.STYLING.DETAILED
  },

  // No icon preset for minimal designs
  MINIMAL: {
    showIcon: false,
    iconType: GMB_LOCATION_CONFIG.ICON_TYPES.NONE,
    addressFormat: GMB_LOCATION_CONFIG.ADDRESS_FORMATS.CITY_STATE,
    styling: GMB_LOCATION_CONFIG.STYLING.DEFAULT
  }
}

/**
 * Helper function to get preset configuration
 * @param {string} presetName - Name of the preset
 * @returns {Object} Preset configuration
 */
export const getGMBPreset = (presetName) => {
  return GMB_PRESETS[presetName] || GMB_PRESETS.DEFAULT
}

// Backward compatibility - maintain existing function signature
export { createGMBLocationOptions as gmbLocationOptions }
